import React, { useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import MaterialIcon from 'react/elements/MaterialIcon';
import TextInput from 'react/elements/TextInput';
import OptionsMenu from './OptionsMenu';
import MealIdeaRow from './MealIdeaRow';
import NewMealIdeaInput from './NewMealIdeaInput';
import { CALENDAR_DRAG_TYPE, mealIdeaDraggableId, mealIdeaGroupDroppableId } from 'react/components/calendar/dragTypes';
import { calendarDropStyle } from 'react/components/calendar/pointerDropTarget';
import { addMealIdeaGroupToActiveList, saveMealIdeaGroup, MEAL_IDEAS_LOADED } from 'redux/actions/mealIdeas';
import { selectMealIdeasIndex } from '../../../redux/food/selectors';
import useOffline, { onlineOnly, onlineOnlyField, useSaveWhenOnline } from '../../hooks/useOffline';

/**
 * A group of meal ideas, or with `groupId` null the "Ungrouped" section: the ideas that are in no
 * group, passed in as `ideaIds`. Every row can be dragged onto a calendar meal slot, which is why
 * the rows use the calendar's drag type. Within real groups a row can be reordered, and one dropped
 * into another group moves there, or from Ungrouped is added to it (`DashboardPage.onDragEnd`
 * handles all three). Ungrouped is sorted by name and takes no drops.
 *
 * `onOpenIdea` is passed on to each row.
 *
 * Given `matchIds`, the list's search is narrowing it: only those ideas show, the group is open
 * whatever its toggle says, nothing can be dropped into it, and a group with no match is not drawn.
 *
 * The header's name and chevron are one button that collapses the group; the count and the menu sit
 * outside it so they never toggle it.
 *
 * The menu's entries and drops into the group are online-only, so offline the entries are disabled,
 * titled "Needs a connection", and the group takes no drops. Its ideas can still be dragged onto the
 * calendar, which works offline.
 */
export default function MealIdeaGroup({ groupId = null, ideaIds = [], matchIds = null, onRequestDelete, onOpenIdea = null }) {
	const dispatch = useDispatch();
	const group = useSelector((state) => (groupId === null ? null : selectMealIdeasIndex(state).groupsById[groupId]));
	const allIdeas = useSelector((state) => {
		const index = selectMealIdeasIndex(state);
		const ids = groupId === null ? ideaIds : (index.groupsById[groupId]?.meal_idea_ids || []);
		return ids.map((id) => index.ideasById[id]).filter(Boolean);
	}, shallowEqual);
	const ideas = matchIds ? allIdeas.filter((idea) => matchIds.has(idea.id)) : allIdeas;

	const [expanded, setExpanded] = useState(true);
	const [renaming, setRenaming] = useState(false);
	const [draftName, setDraftName] = useState('');
	const [addResult, setAddResult] = useState(null);
	const offline = useOffline();
	const whenOnline = useSaveWhenOnline(offline);

	if((groupId !== null && !group) || (matchIds && !ideas.length)) {
		return null;
	}
	const open = expanded || !!matchIds;

	const startRename = () => {
		setDraftName(group.name);
		setRenaming(true);
	};

	const finishRename = () => {
		const trimmed = draftName.trim();
		if(!trimmed || trimmed === group.name) {
			setRenaming(false);
			return;
		}
		dispatch(saveMealIdeaGroup(trimmed, group.id)).then((action) => {
			if(action && action.type === MEAL_IDEAS_LOADED) {
				setRenaming(false);
			}
		});
	};

	const addGroupToGroceryList = () => {
		setAddResult({ ok: true, text: 'Adding to the grocery list...' });
		dispatch(addMealIdeaGroupToActiveList(group.id)).then((succeeded) => {
			setAddResult(succeeded ?
				{ ok: true, text: 'Added every idea here to the grocery list.' } :
				{ ok: false, text: 'That did not reach the grocery list. Try again.' });
		});
	};

	const rows = (
		<Droppable
			droppableId={mealIdeaGroupDroppableId(groupId)}
			type={CALENDAR_DRAG_TYPE}
			isDropDisabled={!group || !!matchIds || offline}
		>
			{(provided) => (
				<div className='meal_idea_group__ideas' ref={provided.innerRef} {...provided.droppableProps}>
					{ideas.map((idea, index) => (
						<Draggable
							key={idea.id}
							draggableId={mealIdeaDraggableId(groupId, idea.id)}
							index={index}
						>
							{(draggable, snapshot) => (
								<div
									ref={draggable.innerRef}
									{...draggable.draggableProps}
									style={calendarDropStyle(draggable.draggableProps.style, snapshot)}
								>
									<MealIdeaRow
										idea={idea}
										groupId={groupId}
										dragHandleProps={draggable.dragHandleProps}
										onRequestDelete={onRequestDelete}
										onOpen={onOpenIdea}
									/>
								</div>
							)}
						</Draggable>
					))}
					{provided.placeholder}
					{group && !matchIds && !ideas.length && <p className='meal_idea_group__empty'>Drag ideas here, or add one below.</p>}
				</div>
			)}
		</Droppable>
	);

	const name = group ? group.name : 'Ungrouped';

	return (
		<section className={`meal_idea_group ${group ? '' : 'is-ungrouped'} ${open ? '' : 'is-collapsed'} ${renaming ? 'is-renaming' : ''}`}>
			<header className='meal_idea_group__header'>
				<button
					type='button'
					className='btn-flat meal_idea_group__toggle'
					aria-expanded={open}
					title={open ? `Collapse ${name}` : `Expand ${name}`}
					onClick={() => setExpanded((current) => !current)}
				>
					<MaterialIcon className='meal_idea_group__chevron'>expand_more</MaterialIcon>
					{!renaming && <span className='group_name'>{name}</span>}
				</button>
				{renaming &&
				<span className='group_name input-field'>
					<TextInput
						autoFocus
						{...onlineOnlyField(offline)}
						value={draftName}
						onChange={(event) => setDraftName(event.target.value)}
						onBlur={() => whenOnline('rename', finishRename)}
						onKeyDown={(event) => {
							if(event.key === 'Enter') {
								event.preventDefault();
								whenOnline('rename', finishRename);
							}
							if(event.key === 'Escape') {
								setRenaming(false);
							}
						}}
					/>
				</span>
				}
				<span className='meal_idea_group__count' title={`${ideas.length} ${ideas.length === 1 ? 'idea' : 'ideas'}`}>
					{ideas.length}
				</span>
				{group &&
				<OptionsMenu
					id={`meal_idea_group_menu_${group.id}`}
					items={[
						{ content: <span>Add all to grocery list</span>, onClick: addGroupToGroceryList, ...onlineOnly(offline) },
						{ content: <span>Rename group</span>, onClick: startRename, ...onlineOnly(offline) },
						{ content: <span>Delete {group.name}</span>, onClick: () => onRequestDelete('group', group.id, group.name), ...onlineOnly(offline) },
					]}
				/>
				}
			</header>
			{open &&
			<div className='meal_idea_group__body'>
				{!!addResult && <p className={`meal_idea_group__status ${addResult.ok ? '' : 'is-error'}`}>{addResult.text}</p>}
				{rows}
				{!matchIds && <NewMealIdeaInput groupId={groupId} />}
			</div>
			}
		</section>
	);
}
